import { Link } from "react-router-dom";
import { LucideMoreHorizontal, LucideTrash2, LucideCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

function truncateOen(raw) {
  const trimmed = raw?.trim() ?? "";
  if (!trimmed) return null;
  return trimmed.length > 9 ? trimmed.slice(0, 9) + "…" : trimmed;
}

function truncateName(name) {
  if (!name?.trim()) return "Unnamed student";
  return name.length > 30 ? name.slice(0, 30) + "…" : name;
}

function CheckToggle({ card, isComplete, onToggleComplete, canEdit = true, sm }) {
  return (
    <div className="shrink-0" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
      <button
        onClick={canEdit ? () => onToggleComplete?.(card) : undefined}
        disabled={!canEdit}
        aria-label={isComplete ? "Marked complete" : "Not complete"}
        className={`rounded-full flex items-center justify-center transition-all duration-150 ${
          sm ? "w-6 h-6" : "w-11 h-11"
        } ${
          isComplete
            ? "bg-brand-700 border-transparent dark:bg-brand-500"
            : "bg-white border border-neutral-200 dark:bg-transparent dark:border-white/20"
        } ${
          canEdit
            ? isComplete
              ? "hover:bg-brand-600 dark:hover:bg-brand-400"
              : "hover:bg-neutral-50 dark:hover:bg-white/10"
            : "cursor-not-allowed opacity-80"
        }`}
      >
        <LucideCheck
          size={sm ? 13 : 20}
          strokeWidth={isComplete ? 2.5 : 1.5}
          className={isComplete ? "text-white" : "text-neutral-200 dark:text-white/20"}
        />
      </button>
    </div>
  );
}

function CardMenu({ card, onDelete, sm }) {
  return (
    <div
      className="shrink-0 z-10 [&_button]:text-neutral-400 [&_button:hover]:text-neutral-700 [&_button:hover]:bg-neutral-100 [&_button]:rounded-lg [&_button]:transition-colors dark:[&_button]:text-white/80 dark:[&_button:hover]:text-white dark:[&_button:hover]:bg-white/15"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" aria-label="Report actions" className={sm ? "h-6 w-6" : "h-8 w-8"}>
            <LucideMoreHorizontal size={sm ? 13 : 15} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onClick={() => onDelete?.(card)}
          >
            <LucideTrash2 size={13} className="mr-2" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export function StudentReportCard({
  card,
  reportFolderId,
  linkPath,
  linkState,
  size = "md",
  isComplete = false,
  onToggleComplete,
  canEdit = true,
  onDelete,
  showCheck = true,
  showOen = true,
  showName = true,
  showMenu = true,
  onClick,
}) {
  const oenDisplay = truncateOen(card.data?.oen);
  const sm = size === "sm";

  const resolvedLinkPath =
    linkPath ?? (reportFolderId ? `/report-folders/${reportFolderId}/reports/${card._id}` : undefined);

  if (sm) {
    return (
      <Card hoverable className="overflow-hidden flex items-center">
        {showCheck && (
          <div className="pl-3 shrink-0 border-r border-neutral-200 dark:border-white/10">
            <div className="pr-3 py-1.5">
              <CheckToggle card={card} isComplete={isComplete} onToggleComplete={onToggleComplete} canEdit={canEdit} sm />
            </div>
          </div>
        )}
        <Link
          to={resolvedLinkPath}
          state={linkState}
          className="flex-1 flex items-center px-3 py-1.5 gap-3 min-w-0 hover:bg-neutral-50 dark:hover:bg-white/5 transition-colors"
          onClick={onClick}
        >
          {showOen && (
            <span className={`w-[80px] shrink-0 block text-center text-xs font-mono font-bold rounded-md px-2 py-0.5 ${
              oenDisplay
                ? "bg-brand-50 border border-brand-200 text-brand-700 dark:bg-white/10 dark:border-white/20 dark:text-white/90"
                : "bg-brand-50/50 border border-brand-100 text-neutral-400 italic dark:bg-white/10 dark:border-white/20 dark:text-white/30"
            }`}>
              {oenDisplay ?? "No OEN"}
            </span>
          )}
          {showName && (
            <p className="flex-1 min-w-0 text-sm font-medium text-neutral-900 dark:text-white truncate">
              {truncateName(card.studentName)}
            </p>
          )}
        </Link>
        {showMenu && (
          <div className="pr-2 shrink-0 border-l border-neutral-200 dark:border-white/10">
            <div className="pl-2">
              <CardMenu card={card} onDelete={onDelete} sm />
            </div>
          </div>
        )}
      </Card>
    );
  }

  return (
    <Card hoverable className="rounded-2xl overflow-hidden flex items-center">
      {showCheck && (
        <div className="pl-6 shrink-0 border-r border-neutral-200 dark:border-white/10">
          <div className="pr-6 py-4">
            <CheckToggle card={card} isComplete={isComplete} onToggleComplete={onToggleComplete} canEdit={canEdit} sm={false} />
          </div>
        </div>
      )}
      <Link
        to={resolvedLinkPath}
        state={linkState}
        className="flex-1 flex items-center px-6 py-4 gap-5 min-w-0 hover:bg-neutral-50 dark:hover:bg-white/5 transition-colors"
        onClick={onClick}
      >
        {showOen && (
          <div className="w-[108px] shrink-0">
            {oenDisplay ? (
              <span className="block text-center text-[15px] bg-brand-50 border border-brand-200 text-brand-700 dark:bg-white/10 dark:border-white/20 dark:text-white/100 rounded-md px-2 py-0.5">
                {oenDisplay}
              </span>
            ) : (
              <span className="block text-center text-[15px] bg-brand-50/50 border border-brand-100 text-neutral-400 italic dark:bg-white/10 dark:border-white/20 dark:text-white/30 rounded-md px-2 py-0.5">
                No OEN
              </span>
            )}
          </div>
        )}
        {showName && (
          <p className="flex-1 min-w-0 text-base font-semibold text-neutral-900 dark:text-white leading-snug truncate">
            {truncateName(card.studentName)}
          </p>
        )}
      </Link>
      {showMenu && (
        <div className="pr-4 shrink-0 border-l border-neutral-200 dark:border-white/10">
          <div className="pl-4">
            <CardMenu card={card} onDelete={onDelete} sm={false} />
          </div>
        </div>
      )}
    </Card>
  );
}
